import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
    Box, Card, CardHeader, Container, Grid, IconButton,
    Stack, Tooltip, Typography, Button, CircularProgress, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import Page from 'src/components/Page';
import { apiInstance } from 'src/utils/apiAuth';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';
import MasterNodeDialog from './MasterNodeDialog';

// ----------------------------------------------------------------------
// Level metadata: REST path + the child-list query param used to load it.
const LEVELS = {
    practice: { title: 'Practice', path: 'practice/', childParam: 'practice_id' },
    offering: { title: 'Offering', path: 'offering/', childParam: 'offering_id' },
    'segment-master': { title: 'Segment Master', path: 'segment-master/', childParam: 'segment_master_id' },
    segment: { title: 'Segment', path: 'segment/', childParam: null },
};

// ----------------------------------------------------------------------
// One cascading column. Presentational only (no data fetching / hooks).
function MasterColumn({
    title, nodes, loading, selectedId, selectable,
    addDisabled, addHint, onAdd, onSelect, onEdit, onToggle,
}) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardHeader
                title={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>}
                action={(
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={onAdd}
                        disabled={addDisabled}
                        sx={{ backgroundColor: '#00AB55' }}
                    >
                        Add
                    </Button>
                )}
                sx={{ pb: 1 }}
            />
            <Divider />
            <Box sx={{ p: 1, minHeight: 200, maxHeight: 480, overflowY: 'auto' }}>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                    </Stack>
                ) : addDisabled && nodes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        {addHint}
                    </Typography>
                ) : nodes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        No records.
                    </Typography>
                ) : (
                    nodes.map((node) => {
                        const isSelected = selectable && selectedId === node.id;
                        return (
                            <Box
                                key={node.id}
                                onClick={() => selectable && onSelect(node)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: 1,
                                    cursor: selectable ? 'pointer' : 'default',
                                    backgroundColor: isSelected ? 'rgba(0, 171, 85, 0.12)' : 'transparent',
                                    '&:hover': { backgroundColor: isSelected ? 'rgba(0, 171, 85, 0.16)' : 'action.hover' },
                                }}
                            >
                                {node.is_active
                                    ? <CheckCircleIcon fontSize="small" color="success" />
                                    : <CancelIcon fontSize="small" color="error" />}
                                <Box sx={{ flexGrow: 1, ml: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{ fontWeight: isSelected ? 700 : 400 }}
                                    >
                                        {node.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Order: {node.order}
                                    </Typography>
                                </Box>
                                <Tooltip title="Edit">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); onEdit(node); }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={node.is_active ? 'Deactivate' : 'Activate'}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); onToggle(node); }}
                                    >
                                        {node.is_active
                                            ? <ToggleOnIcon fontSize="small" color="success" />
                                            : <ToggleOffIcon fontSize="small" color="disabled" />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        );
                    })
                )}
            </Box>
        </Card>
    );
}

MasterColumn.propTypes = {
    title: PropTypes.string.isRequired,
    nodes: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selectable: PropTypes.bool,
    addDisabled: PropTypes.bool,
    addHint: PropTypes.string,
    onAdd: PropTypes.func.isRequired,
    onSelect: PropTypes.func,
    onEdit: PropTypes.func.isRequired,
    onToggle: PropTypes.func.isRequired,
};

// ----------------------------------------------------------------------
export default function MasterData() {
    const [practices, setPractices] = useState([]);
    const [offerings, setOfferings] = useState([]);
    const [segmentMasters, setSegmentMasters] = useState([]);
    const [segments, setSegments] = useState([]);
    // All segment masters under the selected practice — options for the multi-target "segment" add.
    const [segmentMasterOptions, setSegmentMasterOptions] = useState([]);

    const [loading, setLoading] = useState({
        practice: false, offering: false, 'segment-master': false, segment: false,
    });

    const [selectedPractice, setSelectedPractice] = useState(null);
    const [selectedOffering, setSelectedOffering] = useState(null);
    const [selectedSegmentMaster, setSelectedSegmentMaster] = useState(null);

    const [dialog, setDialog] = useState({
        open: false, mode: 'add', level: 'practice', initialValues: {}, parentLabel: '', editId: null,
    });

    const setLevelLoading = (level, value) =>
        setLoading((prev) => ({ ...prev, [level]: value }));

    // ---- fetchers (no active_only param: management view shows active + inactive) ----
    const fetchPractices = () => {
        setLevelLoading('practice', true);
        apiInstance.get('practice/')
            .then((res) => setPractices(res?.data?.data || []))
            .catch((e) => showErrorToast(e))
            .finally(() => setLevelLoading('practice', false));
    };

    const fetchOfferings = (practiceId) => {
        setLevelLoading('offering', true);
        apiInstance.get(`offering/?practice_id=${practiceId}`)
            .then((res) => setOfferings(res?.data?.data || []))
            .catch((e) => showErrorToast(e))
            .finally(() => setLevelLoading('offering', false));
    };

    const fetchSegmentMasters = (offeringId) => {
        setLevelLoading('segment-master', true);
        apiInstance.get(`segment-master/?offering_id=${offeringId}`)
            .then((res) => setSegmentMasters(res?.data?.data || []))
            .catch((e) => showErrorToast(e))
            .finally(() => setLevelLoading('segment-master', false));
    };

    const fetchSegments = (segmentMasterId) => {
        setLevelLoading('segment', true);
        apiInstance.get(`segment/?segment_master_id=${segmentMasterId}`)
            .then((res) => setSegments(res?.data?.data || []))
            .catch((e) => showErrorToast(e))
            .finally(() => setLevelLoading('segment', false));
    };

    // All segment masters under a practice (across its offerings) — multi-target options for "segment".
    const fetchSegmentMasterOptions = (practiceId) => {
        apiInstance.get(`segment-master/?practice_id=${practiceId}`)
            .then((res) => setSegmentMasterOptions(res?.data?.data || []))
            .catch((e) => showErrorToast(e));
    };

    useEffect(() => {
        fetchPractices();
    }, []);

    // ---- cascading selection: pick a parent -> clear downstream, load children ----
    const handleSelectPractice = (node) => {
        setSelectedPractice(node);
        setSelectedOffering(null);
        setSelectedSegmentMaster(null);
        setSegmentMasters([]);
        setSegments([]);
        fetchOfferings(node.id);
    };

    const handleSelectOffering = (node) => {
        setSelectedOffering(node);
        setSelectedSegmentMaster(null);
        setSegments([]);
        fetchSegmentMasters(node.id);
    };

    const handleSelectSegmentMaster = (node) => {
        setSelectedSegmentMaster(node);
        fetchSegments(node.id);
    };

    // ---- re-fetch ONLY the affected column, preserving drill-down state (FR-013) ----
    const refetchLevel = (level) => {
        if (level === 'practice') fetchPractices();
        else if (level === 'offering' && selectedPractice) fetchOfferings(selectedPractice.id);
        else if (level === 'segment-master' && selectedOffering) fetchSegmentMasters(selectedOffering.id);
        else if (level === 'segment' && selectedSegmentMaster) fetchSegments(selectedSegmentMaster.id);
    };

    // ---- dialog open helpers ----
    const openAddDialog = (level) => {
        const initialValues = { label: '', order: '', is_active: true };
        let parentLabel = '';
        if (level === 'offering') {
            initialValues.practice = selectedPractice ? selectedPractice.id : '';
        } else if (level === 'segment-master') {
            initialValues.offering = selectedOffering ? selectedOffering.id : '';
            // Pre-check the current offering for the multi-target stamp (US-1).
            initialValues.offerings = selectedOffering ? [selectedOffering.id] : [];
            parentLabel = selectedOffering ? selectedOffering.label : '';
        } else if (level === 'segment') {
            initialValues.segment_master = selectedSegmentMaster ? selectedSegmentMaster.id : '';
            // Pre-check the current master for the multi-target stamp (US-2).
            initialValues.segment_masters = selectedSegmentMaster ? [selectedSegmentMaster.id] : [];
            parentLabel = selectedSegmentMaster ? selectedSegmentMaster.label : '';
            // Load all masters under this practice as options for the multi-select.
            if (selectedPractice) fetchSegmentMasterOptions(selectedPractice.id);
        }
        setDialog({ open: true, mode: 'add', level, initialValues, parentLabel, editId: null });
    };

    const openEditDialog = (level, node) => {
        const initialValues = {
            label: node.label,
            order: node.order,
            is_active: node.is_active,
        };
        let parentLabel = '';
        if (level === 'offering') {
            initialValues.practice = node.practice;
        } else if (level === 'segment-master') {
            initialValues.offering = node.offering;
            parentLabel = node.offering_label || (selectedOffering ? selectedOffering.label : '');
        } else if (level === 'segment') {
            initialValues.segment_master = node.segment_master;
            parentLabel = node.segment_master_label || (selectedSegmentMaster ? selectedSegmentMaster.label : '');
        }
        setDialog({ open: true, mode: 'edit', level, initialValues, parentLabel, editId: node.id });
    };

    const closeDialog = () => setDialog((prev) => ({ ...prev, open: false }));

    // ---- create / edit submit (payload already carries name + parent FK) ----
    const handleDialogSubmit = (payload) => {
        const { level, mode, editId } = dialog;
        const { path } = LEVELS[level];
        const request = mode === 'edit'
            ? apiInstance.put(`${path}${editId}/`, payload)
            : apiInstance.post(path, payload);

        request
            .then((res) => {
                const data = res?.data?.data;
                // Multi-target response (FR-008): summarize created + skipped copies.
                if (data && Array.isArray(data.created)) {
                    const skipped = data.skipped || [];
                    showToast(`Created ${data.created.length}${skipped.length ? `, skipped ${skipped.length}` : ''}`);
                    if (skipped.length) {
                        const names = skipped
                            .map((s) => s.offering_label || s.segment_master_label || 'target')
                            .join(', ');
                        showErrorToast(`Skipped: ${names}`);
                    }
                } else {
                    showToast(res?.data?.message);
                }
                closeDialog();
                refetchLevel(level);
            })
            .catch((e) => showErrorToast(e));
    };

    // ---- soft activate/deactivate: PUT full payload with is_active flipped (Constraint #2) ----
    // Full payload is required because the Practice/Offering serializers read name/label/order
    // via direct key access on update (a partial PUT would 500).
    const handleToggle = (level, node) => {
        const payload = {
            label: node.label,
            name: (node.label || '').toUpperCase(),
            order: node.order,
            is_active: !node.is_active,
        };
        if (level === 'offering') payload.practice = node.practice;
        else if (level === 'segment-master') payload.offering = node.offering;
        else if (level === 'segment') payload.segment_master = node.segment_master;

        apiInstance.put(`${LEVELS[level].path}${node.id}/`, payload)
            .then((res) => {
                showToast(res?.data?.message);
                refetchLevel(level);
            })
            .catch((e) => showErrorToast(e));
    };

    return (
        <Page title="Master Data | CRM">
            <Container maxWidth="xl">
                <Typography variant="h4" align="center" sx={{ fontWeight: 800, mb: 1 }}>
                    Master Data
                </Typography>
                <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
                    Practice → Offering → Segment Master → Segment. Select a node to drill down.
                </Typography>

                <Grid container spacing={2} alignItems="stretch">
                    <Grid item xs={12} md={3}>
                        <MasterColumn
                            title={LEVELS.practice.title}
                            nodes={practices}
                            loading={loading.practice}
                            selectedId={selectedPractice ? selectedPractice.id : null}
                            selectable
                            addDisabled={false}
                            onAdd={() => openAddDialog('practice')}
                            onSelect={handleSelectPractice}
                            onEdit={(node) => openEditDialog('practice', node)}
                            onToggle={(node) => handleToggle('practice', node)}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <MasterColumn
                            title={LEVELS.offering.title}
                            nodes={offerings}
                            loading={loading.offering}
                            selectedId={selectedOffering ? selectedOffering.id : null}
                            selectable
                            addDisabled={!selectedPractice}
                            addHint="Select a Practice to view its Offerings."
                            onAdd={() => openAddDialog('offering')}
                            onSelect={handleSelectOffering}
                            onEdit={(node) => openEditDialog('offering', node)}
                            onToggle={(node) => handleToggle('offering', node)}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <MasterColumn
                            title={LEVELS['segment-master'].title}
                            nodes={segmentMasters}
                            loading={loading['segment-master']}
                            selectedId={selectedSegmentMaster ? selectedSegmentMaster.id : null}
                            selectable
                            addDisabled={!selectedOffering}
                            addHint="Select an Offering to view its Segment Masters."
                            onAdd={() => openAddDialog('segment-master')}
                            onSelect={handleSelectSegmentMaster}
                            onEdit={(node) => openEditDialog('segment-master', node)}
                            onToggle={(node) => handleToggle('segment-master', node)}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <MasterColumn
                            title={LEVELS.segment.title}
                            nodes={segments}
                            loading={loading.segment}
                            selectable={false}
                            addDisabled={!selectedSegmentMaster}
                            addHint="Select a Segment Master to view its Segments."
                            onAdd={() => openAddDialog('segment')}
                            onEdit={(node) => openEditDialog('segment', node)}
                            onToggle={(node) => handleToggle('segment', node)}
                        />
                    </Grid>
                </Grid>
            </Container>

            {dialog.open && (
                <MasterNodeDialog
                    open={dialog.open}
                    mode={dialog.mode}
                    level={dialog.level}
                    initialValues={dialog.initialValues}
                    parentOptions={practices}
                    parentLabel={dialog.parentLabel}
                    offeringOptions={offerings}
                    masterOptions={segmentMasterOptions}
                    onSubmit={handleDialogSubmit}
                    onClose={closeDialog}
                />
            )}
        </Page>
    );
}
